import { Autorenew } from '@material-ui/icons'
import React, { useState, useReducer, useEffect } from 'react'
import {
  useMutation,
  useNotify,
  useRecordContext,
  useRefresh,
  useShowContext,
} from 'react-admin'
import {
  engineLockButtonInitialState,
  engineLockButtonReducer,
} from '../../../../core/devices/engines/engine-lock-button.reducer'
import { ToggleType } from '../../../../core/devices/engines/engine-lock-button'
import { EngineDto } from '../../../../core/dto/device/engine/engine.dto'
import Button from '../../../common/customized-mui-components/Button'
import { EngineLockDialog } from '../EngineDialogs'
import { useHasAuthority } from '../../../hooks/useHasAuthority'
import { Authority } from '../../../../core/auth/Authority'
import { ConfirmDialog } from '../../../common/ConfirmDialog'
import { ResourceName } from '../../../../core/ResourceName'
import { DateTime } from '../../../../core/common/date-time'

export const EngineLockButton = ({ ...props }) => {
  const { asRecurringJob, recurringMethod, ...rest } = props
  const notify = useNotify()
  const hasAuthority = useHasAuthority()
  const { loading } = useShowContext()
  const [{ type, text, mutateQuery }, dispatch] = useReducer(
    engineLockButtonReducer,
    engineLockButtonInitialState,
  )
  const [lockDialogOpen, setLockDialogOpen] = useState(false)
  const [disabledId, setDisabledId] = useState(0)
  const [unlockDialogOpen, setUnlockDialogOpen] = useState(false)
  const engine = useRecordContext<EngineDto>()
  const [mutate, { loading: mutationLoading }] = useMutation()
  const refresh = useRefresh()

  useEffect(() => {
    if (
      (!asRecurringJob && engine.disabledId) ||
      (asRecurringJob && recurringMethod === ToggleType.UNLOCK)
    ) {
      dispatch({ type: ToggleType.UNLOCK })
    } else {
      dispatch({ type: ToggleType.LOCK })
    }
  }, [asRecurringJob, engine, recurringMethod])

  const onClick = async () => {
    if (type === ToggleType.UNLOCK) setUnlockDialogOpen(true)
    else setLockDialogOpen(true)
  }

  const handleLock = async (
    recurringJobName?: string,
    cronExpression?: string,
    scheduledAt?: DateTime,
  ) => {
    let query
    if (!asRecurringJob) {
      query = mutateQuery?.(engine, disabledId)
    } else {
      query = mutateQuery?.(
        engine,
        disabledId,
        recurringJobName as string,
        cronExpression as string,
        scheduledAt as DateTime,
      )
    }
    await mutate(query, {
      returnPromise: true,
      onFailure: (err) => notify(err?.message, 'error'),
    })
    refresh()
  }

  return (
    <>
      <ConfirmDialog
        open={unlockDialogOpen}
        confirm={handleLock}
        source="lock"
        cancel={() => setUnlockDialogOpen(false)}
        action="remove"
        resource={ResourceName.ENGINES}
        asRecurringJob={asRecurringJob}
      />
      <EngineLockDialog
        open={lockDialogOpen}
        close={() => setLockDialogOpen(false)}
        disabledId={disabledId}
        setDisabledId={setDisabledId}
        handleLock={handleLock}
        asRecurringJob={asRecurringJob}
      />
      <Button
        variant="contained"
        color="primary"
        label={!asRecurringJob ? text : `${text}-recurring`}
        useSmallVersionBreakpoint={false}
        startIcon={asRecurringJob ? <Autorenew /> : undefined}
        disabled={
          !hasAuthority(Authority.ENGINES_LOCK_BUTTON) ||
          loading ||
          mutationLoading
        }
        onClick={onClick}
        {...rest}
      />
    </>
  )
}
